import { reactive, watch, onMounted, toRefs, defineComponent, computed } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import { debounce } from 'perfect-debounce';

function _extends$2() {
    _extends$2 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$2.apply(this, arguments);
}
function _object_without_properties_loose$1(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var autocompletionRequestBuilder = ((autocompletionRequest, input, sessionToken)=>{
    const { bounds, location } = autocompletionRequest, rest = _object_without_properties_loose$1(autocompletionRequest, [
        "bounds",
        "location"
    ]);
    const res = _extends$2({
        input
    }, rest);
    if (sessionToken) res.sessionToken = sessionToken;
    if (bounds) res.bounds = new google.maps.LatLngBounds(...bounds);
    if (location) res.location = new google.maps.LatLng(location);
    return res;
});

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
function usePlacesAutocomplete(query, { apiKey = '', apiOptions = {}, autocompletionRequest = {}, debounce: debounce$1 = 300, minLengthAutocomplete = 0, onLoadFailed = console.error, withSessionToken } = {}) {
    const state = reactive({
        placesService: undefined,
        sessionToken: undefined,
        suggestions: [],
        loading: false
    });
    const fetchSuggestions = ()=>{
        var _state_placesService;
        if (!state.placesService) {
            state.suggestions = [];
            return;
        }
        if (query.value.length < minLengthAutocomplete || !query.value.length) {
            state.suggestions = [];
            return;
        }
        const autocompletionReq = _extends$1({}, autocompletionRequest);
        state.loading = true;
        (_state_placesService = state.placesService) == null ? void 0 : _state_placesService.getPlacePredictions(autocompletionRequestBuilder(autocompletionReq, query.value, withSessionToken && state.sessionToken), (result)=>{
            state.suggestions = result || [];
            state.loading = false;
        });
    };
    watch(query, debounce(fetchSuggestions, debounce$1));
    const refreshSessionToken = ()=>{
        state.sessionToken = new google.maps.places.AutocompleteSessionToken();
    };
    const initializeService = ()=>{
        if (!window.google) {
            console.error('[vue-use-places-autocomplete]: Google script not loaded');
            return;
        }
        if (!window.google.maps) {
            console.error('[vue-use-places-autocomplete]: Google maps script not loaded');
            return;
        }
        if (!window.google.maps.places) {
            console.error('[vue-use-places-autocomplete]: Google maps places script not loaded');
            return;
        }
        state.placesService = new window.google.maps.places.AutocompleteService();
        refreshSessionToken();
        fetchSuggestions();
    };
    onMounted(()=>{
        const init = /*#__PURE__*/ _async_to_generator(function*() {
            try {
                if (!window.google || !window.google.maps || !window.google.maps.places) yield new Loader(_extends$1({
                    apiKey
                }, _extends$1({
                    libraries: [
                        'places'
                    ]
                }, apiOptions))).load();
                initializeService();
            } catch (error) {
                onLoadFailed(error);
            }
        });
        if (apiKey) init();
        else initializeService();
    });
    return _extends$1({}, toRefs(state), {
        refreshSessionToken
    });
}

function geocodeByLatLng(latLng) {
    const geocoder = new window.google.maps.Geocoder();
    const { OK } = window.google.maps.GeocoderStatus;
    return new Promise((resolve, reject)=>{
        geocoder.geocode({
            location: latLng
        }, (results, status)=>{
            if (status !== OK) return reject(status);
            return resolve(results);
        });
    });
}

function geocodeByAddress(address) {
    const geocoder = new window.google.maps.Geocoder();
    const { OK } = window.google.maps.GeocoderStatus;
    return new Promise((resolve, reject)=>{
        geocoder.geocode({
            address
        }, (results, status)=>{
            if (status !== OK) return reject(status);
            return resolve(results);
        });
    });
}

function geocodeByPlaceId(placeId) {
    const geocoder = new window.google.maps.Geocoder();
    const { OK } = window.google.maps.GeocoderStatus;
    return new Promise((resolve, reject)=>{
        geocoder.geocode({
            placeId
        }, (results, status)=>{
            if (status !== OK) return reject(status);
            return resolve(results);
        });
    });
}

function getLatLng(result) {
    return new Promise((resolve, reject)=>{
        try {
            const latLng = {
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng()
            };
            return resolve(latLng);
        } catch (e) {
            return reject(e);
        }
    });
}

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const GooglePlacesAutocomplete = defineComponent({
    name: 'GooglePlacesAutocomplete',
    props: {
        query: {
            type: String,
            default: ''
        },
        apiKey: {
            type: String,
            default: '',
            required: false
        },
        apiOptions: {
            type: Object,
            default: ()=>({}),
            required: false
        },
        autocompletionRequest: {
            type: Object,
            default: ()=>({}),
            required: false
        },
        debounce: {
            type: Number,
            default: 300,
            required: false
        },
        minLengthAutocomplete: {
            type: Number,
            default: 0,
            required: false
        },
        withSessionToken: {
            type: Boolean,
            required: false
        }
    },
    emits: [
        'loadFailed'
    ],
    setup (props, { slots, expose, emit }) {
        const rest = _object_without_properties_loose(props, [
            "query"
        ]);
        const query = computed(()=>props.query);
        const { refreshSessionToken, sessionToken, suggestions, loading } = usePlacesAutocomplete(query, _extends({}, rest, {
            onLoadFailed (error) {
                emit('loadFailed', error);
            }
        }));
        expose({
            refreshSessionToken,
            getSessionToken () {
                return sessionToken.value;
            }
        });
        return ()=>slots.default({
                suggestions: suggestions.value,
                loading: loading.value
            });
    }
});

export { GooglePlacesAutocomplete, geocodeByAddress, geocodeByLatLng, geocodeByPlaceId, getLatLng, usePlacesAutocomplete };
